// ── consensusTrend — sub-cent trend/recency engine for Unabated consensus ──
//
// Hydra's consensus frames carry fair probabilities at tenth-of-a-cent
// resolution (`awayFairProbTenths` etc., integers 0–1000, sides summing to
// 1000) alongside the unchanged integer cents that remain the DISPLAYED
// price. Frames arrive on tenth-only moves too (coalesced to ≤1 per
// game/period per 250ms), so a frame's integer fields can be identical to
// the previous frame's — every frame is still an update. See HYDRA_API.md
// → "Unabated consensus update".
//
// The tenths are ABSOLUTE values, never deltas: under backpressure hydra
// coalesces latest-wins, silently merging frames, so an accumulated delta
// stream would corrupt. This module therefore derives movement by diffing
// consecutive received frames' tenths and keeps a trailing ~10s window of
// per-side {t, delta} samples per (gameKey, periodTypeId, marketType)
// bucket. Callers own the bucketing (marketStore for the WS channel,
// oddsStreamHub for the per-game SSE); this module is pure state-in /
// state-out so both paths share one implementation and it unit-tests
// without a store.
//
// Three reads come out of the state, per side (away/home/draw):
//   • recency  — 0..1, exp(−age/RECENCY_TAU_MS) since the last frame
//                ARRIVAL for the bucket (a tenth-only frame counts even
//                though the integer didn't change). Drives the number's
//                brightness: full on arrival, back to baseline in ~10s.
//   • direction — sign of the net tenths drift over the trailing window
//                while samples are live; once the window drains it falls
//                back to the last observed move direction for a further
//                ARROW_LINGER grace (grey resting arrow), then reads 0 —
//                the arrow hides ~20s after the last move. Renderers keep
//                the slot reserved (invisible spacer at 0) so the
//                show/hide never shifts layout.
//   • strength — recency-weighted absolute delta mass,
//                clamp(Σ|Δᵢ|·exp(−(now−tᵢ)/MASS_TAU_MS) / MASS_FULL_SCALE_TENTHS, 0, 1).
//                A move that's speeding up burns bright, a steady drift
//                sits mid, a stalled line fades out within ~10s.
//
// Snapshot handling: a snapshot re-delivers values computed up to minutes
// ago. Callers date the frame with `arrivalMs` (receipt time minus the
// server-side staleness, i.e. serverTs − lastUpdateMs) so a stale snapshot
// seeds the tenths state dim instead of flashing bright, or pass
// `arrivalMs: null` for an unknown-age seed (prevTenths update only — no
// samples, no recency).

export const CONSENSUS_TREND_WINDOW_MS = 10_000;
/** Recency (behavior A) decay constant — exp(−10s/3.3s) ≈ 0.05, i.e. the
 *  brightness boost is visually gone ~10s after the last frame. */
export const CONSENSUS_RECENCY_TAU_MS = 3_300;
/** Delta-mass (behavior B) decay constant. */
export const CONSENSUS_MASS_TAU_MS = 3_500;
/** Delta mass that saturates the arrow at full brightness: 15 tenths
 *  (1.5¢) of recency-weighted movement. Tunable. */
export const CONSENSUS_MASS_FULL_SCALE_TENTHS = 15;
/** How long the arrow rests grey AFTER the 10s decay window before hiding
 *  — total arrow lifetime per move is WINDOW + LINGER (~20s). */
export const CONSENSUS_ARROW_LINGER_MS = 10_000;

export type ConsensusTrendSide = 'away' | 'home' | 'draw';

export interface ConsensusTrendSample {
  /** Effective arrival time of the frame that produced this delta. */
  t: number;
  /** newTenths − prevReceivedTenths for this side (signed, tenths). */
  delta: number;
}

export interface ConsensusSideTrend {
  /** Last received absolute tenths for this side; null until first seen. */
  prevTenths: number | null;
  /** Trailing-window delta samples, oldest first. Pruned on ingest; readers
   *  must still filter by window (pruning only happens when frames flow). */
  samples: ConsensusTrendSample[];
  /** Direction of the most recent dated move (sign of its delta), persisted
   *  past the trailing window so the arrow can rest grey through the linger
   *  grace instead of vanishing at window drain. 0 = never moved. Undated
   *  seeds (unknown-age snapshots) don't update it — trend facts come from
   *  dated frames only. */
  lastDirection: -1 | 0 | 1;
  /** Arrival time of the most recent dated move for THIS side (0 = never).
   *  Ages the resting arrow: direction reads 0 once
   *  now − lastDeltaMs ≥ WINDOW + ARROW_LINGER (~20s), hiding the arrow.
   *  Per side — a frame that moves away/home but not draw must not keep
   *  the draw arrow alive. */
  lastDeltaMs: number;
}

export interface ConsensusTrendState {
  /** Effective arrival time of the most recent recency-worthy frame for
   *  this bucket. 0 = nothing dated yet (e.g. unknown-age seed only). */
  lastFrameMs: number;
  /** Latest sharp-anchor flag (HydraConsensus.sharpBookUsed). null =
   *  unknown — the field was absent (pre-rollout hydra). */
  sharpBookUsed: boolean | null;
  away: ConsensusSideTrend;
  home: ConsensusSideTrend;
  draw: ConsensusSideTrend;
}

/** One consensus frame normalized for ingestion. Sides the sport doesn't
 *  have (draw on binaries) stay null. */
export interface ConsensusTrendFrame {
  awayTenths: number | null;
  homeTenths: number | null;
  drawTenths: number | null;
  sharpBookUsed: boolean | null;
  /** Effective arrival time: receipt time minus any server-side staleness
   *  (snapshot frames re-deliver values computed earlier — date them by
   *  when the value was computed, not when the snapshot was sent). null =
   *  unknown age: seed prevTenths/sharp only, no delta samples, no recency. */
  arrivalMs: number | null;
}

/** Per-side read for rendering (behaviors A + B). */
export interface ConsensusSideGlow {
  /** 0..1 — how recently ANY frame arrived for this bucket. */
  recency: number;
  /** Sign of the net tenths drift over the trailing window while samples
   *  are live; the last observed move direction once the window drains.
   *  0 only before the side has ever moved (render a spacer, not an
   *  arrow) — the arrow is a permanent fixture whose freshness reads
   *  from `strength`, not from mounting/unmounting. */
  direction: -1 | 0 | 1;
  /** 0..1 — recency-weighted |delta| mass; the arrow's tone saturation
   *  (full up/down color → almost fully grey ~10s after the last move). */
  strength: number;
}

/** Normalize a consensus-shaped object (WS `HydraConsensus` or SSE
 *  `OddsStreamConsensus` row) into a trend frame. Falls back to
 *  cents×10 when the tenths fields are absent (pre-rollout hydra) so the
 *  trend degrades to integer grain instead of disappearing — a server
 *  either always or never sends tenths, so the two grains don't mix
 *  within a bucket. */
export function trendFrameFromConsensus(
  c: {
    awayFairProbCents: number;
    homeFairProbCents: number;
    drawFairProbCents?: number | null;
    awayFairProbTenths?: number | null;
    homeFairProbTenths?: number | null;
    drawFairProbTenths?: number | null;
    sharpBookUsed?: boolean | null;
  },
  arrivalMs: number | null,
): ConsensusTrendFrame {
  return {
    awayTenths: c.awayFairProbTenths ?? c.awayFairProbCents * 10,
    homeTenths: c.homeFairProbTenths ?? c.homeFairProbCents * 10,
    drawTenths:
      c.drawFairProbTenths ??
      (c.drawFairProbCents != null ? c.drawFairProbCents * 10 : null),
    sharpBookUsed: c.sharpBookUsed ?? null,
    arrivalMs,
  };
}

function ingestSide(
  prev: ConsensusSideTrend | null,
  tenths: number | null,
  arrivalMs: number | null,
  windowStartMs: number,
): { side: ConsensusSideTrend; changed: boolean; deltaAdded: boolean } {
  const base: ConsensusSideTrend = prev ?? {
    prevTenths: null,
    samples: [],
    lastDirection: 0,
    lastDeltaMs: 0,
  };
  if (tenths == null) {
    // Side not carried by this frame (e.g. draw on a binary) — keep as is.
    return { side: base, changed: false, deltaAdded: false };
  }
  const pruned = base.samples.filter((s) => s.t > windowStartMs);
  if (base.prevTenths === tenths) {
    // Value unchanged; only report a change if pruning shrank the array
    // (so stale samples don't pin the state identity forever).
    if (pruned.length === base.samples.length) {
      return { side: base, changed: false, deltaAdded: false };
    }
    return {
      side: {
        prevTenths: base.prevTenths,
        samples: pruned,
        lastDirection: base.lastDirection,
        lastDeltaMs: base.lastDeltaMs,
      },
      changed: true,
      deltaAdded: false,
    };
  }
  // Value moved. Only produce a dated delta sample when we have both a
  // previous value AND a dated arrival — an unknown-age seed (arrivalMs
  // null) must update prevTenths without fabricating trend, and the very
  // first value for a bucket has nothing to diff against.
  const delta = base.prevTenths != null && arrivalMs != null ? tenths - base.prevTenths : null;
  const samples = delta != null ? [...pruned, { t: arrivalMs!, delta }] : pruned;
  return {
    side: {
      prevTenths: tenths,
      samples,
      // The resting arrow's direction + age anchor: last dated move wins.
      lastDirection: delta != null ? (delta > 0 ? 1 : -1) : base.lastDirection,
      lastDeltaMs: delta != null ? arrivalMs! : base.lastDeltaMs,
    },
    changed: true,
    deltaAdded: delta != null,
  };
}

/**
 * Fold one frame into the bucket's trend state. Returns the previous state
 * object (reference-identical) when nothing material changed, so stores can
 * use reference identity to skip notifications.
 *
 * `passive` marks re-deliveries that are not fresh computes (SSE heartbeats
 * re-carry the latest snapshot every 15s): they update state only if the
 * values actually moved (a catch-up after dropped events), and bump recency
 * only in that case. Non-passive frames bump recency unconditionally —
 * hydra only emits on actual change, so every live frame is an update even
 * when the integers (and occasionally the tenths, post-reconnect) hold.
 */
export function ingestConsensusTrendFrame(
  prev: ConsensusTrendState | null,
  frame: ConsensusTrendFrame,
  nowMs: number,
  opts: { passive?: boolean } = {},
): ConsensusTrendState {
  const windowStartMs = nowMs - CONSENSUS_TREND_WINDOW_MS;
  const away = ingestSide(prev?.away ?? null, frame.awayTenths, frame.arrivalMs, windowStartMs);
  const home = ingestSide(prev?.home ?? null, frame.homeTenths, frame.arrivalMs, windowStartMs);
  const draw = ingestSide(prev?.draw ?? null, frame.drawTenths, frame.arrivalMs, windowStartMs);

  const nextSharp = frame.sharpBookUsed ?? prev?.sharpBookUsed ?? null;
  const sharpChanged = nextSharp !== (prev?.sharpBookUsed ?? null);
  const valuesMoved = away.deltaAdded || home.deltaAdded || draw.deltaAdded;

  // Recency (behavior A) bumps on every dated non-passive frame — hydra
  // emits only on change, so arrival itself is the signal. Passive
  // re-deliveries bump only when they reveal an actual move/flip.
  const bumpRecency =
    frame.arrivalMs != null && (!opts.passive || valuesMoved || sharpChanged);
  const lastFrameMs = bumpRecency
    ? Math.max(prev?.lastFrameMs ?? 0, frame.arrivalMs!)
    : prev?.lastFrameMs ?? 0;

  if (
    prev != null &&
    !away.changed &&
    !home.changed &&
    !draw.changed &&
    !sharpChanged &&
    lastFrameMs === prev.lastFrameMs
  ) {
    return prev;
  }
  return {
    lastFrameMs,
    sharpBookUsed: nextSharp,
    away: away.side,
    home: home.side,
    draw: draw.side,
  };
}

/** Behaviors A + B for one side at `nowMs`. Pure — call per render tick.
 *  `lingerMs` overrides the grey-rest grace after the decay window
 *  (default CONSENSUS_ARROW_LINGER_MS ≈ 10s — the Unabated cadence).
 *  Sparse feeds (OP consensus) pass a LONGER linger so the last move's
 *  direction stays readable between their far-apart updates; the bright
 *  decay itself is unchanged. */
export function readConsensusSideGlow(
  state: ConsensusTrendState,
  side: ConsensusTrendSide,
  nowMs: number,
  lingerMs: number = CONSENSUS_ARROW_LINGER_MS,
): ConsensusSideGlow {
  const recency =
    state.lastFrameMs > 0
      ? Math.min(1, Math.exp(-Math.max(0, nowMs - state.lastFrameMs) / CONSENSUS_RECENCY_TAU_MS))
      : 0;
  const windowStartMs = nowMs - CONSENSUS_TREND_WINDOW_MS;
  let net = 0;
  let mass = 0;
  for (const s of state[side].samples) {
    if (s.t <= windowStartMs) continue;
    net += s.delta;
    mass += Math.abs(s.delta) * Math.exp(-Math.max(0, nowMs - s.t) / CONSENSUS_MASS_TAU_MS);
  }
  // Live window drift wins; a drained (or net-zero) window falls back to
  // the persisted last move for the grey linger grace, then the arrow
  // hides (~20s after the side's last move). Renderers keep the slot
  // reserved so the show/hide never shifts layout.
  const sideState = state[side];
  const arrowExpired =
    sideState.lastDeltaMs <= 0 ||
    nowMs - sideState.lastDeltaMs >= CONSENSUS_TREND_WINDOW_MS + lingerMs;
  return {
    recency,
    direction: net > 0 ? 1 : net < 0 ? -1 : arrowExpired ? 0 : sideState.lastDirection,
    strength: Math.min(1, mass / CONSENSUS_MASS_FULL_SCALE_TENTHS),
  };
}

/** True while the bucket still has anything visibly changing — drives the
 *  fast re-render ticker so idle rows don't pay a 4 Hz subscription. Covers
 *  the full arrow lifetime (10s decay + 10s grey linger + slack): deltas are
 *  always dated ≤ lastFrameMs, so the ticker is guaranteed alive at the
 *  ~20s arrow-hide transition. */
export function isConsensusTrendHot(state: ConsensusTrendState, nowMs: number): boolean {
  return (
    state.lastFrameMs > 0 &&
    nowMs - state.lastFrameMs <
      CONSENSUS_TREND_WINDOW_MS + CONSENSUS_ARROW_LINGER_MS + 2_000
  );
}
